/**
 * Welsh catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Welsh (Cymraeg) at a mathematician's register. **Warrant → «Gwarant»**
 * (warrant). Welsh has the MAXIMAL CLDR cardinal set — zero / one / two / few / many / other
 * (zero 0, one 1, two 2, few 3, many 6, other 4–5 & 7+) — so `frameActive` declares all six
 * (the explicit `=0` gives the empty phrasing). Welsh keeps the noun singular after numerals,
 * with initial mutation (2 → soft, 3/6 → aspirate).
 *
 * NOTE (honesty over guessing): the mutated `frameActive` forms and some technical coinages
 * are best-effort and flagged "expert-review suggested" in `Agent9_REPORT.md`. W/M grades stay
 * Latin (`::numbering-system/latn`). Per INV-STABLE-ID any correction changes only the string.
 */
import { P2 } from '../message-ids-phase2.js';
export const CY_CATALOG = {
    locale: 'cy',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Ymholiad',
        [P2.sectionClaims]: 'Honiadau',
        [P2.sectionIntegrity]: 'Cyfanrwydd',
        [P2.axisWarrantLabel]: 'Gwarant',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirig',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Diffiniad} interp {Dehongliad} testable {Profadwy} tested {Profwyd} refuted {Gwrthbrofwyd} program {Rhaglen ymchwil} philos {Athronyddol} other {Anhysbys}}',
        [P2.axisModalLabel]: 'Moddol',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Lefel cryfder {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Archwiliadwy',
        [P2.stateRefuted]: 'Gwrthbrofwyd',
        [P2.stateOutside]: "Y tu allan i'r cwmpas",
        [P2.verdictConflict]: 'Yn gwrthdaro â {constraints}',
        [P2.verdictOutside]: 'Angen {missing}',
        [P2.lossRegisterPlain]: "Symleiddiwyd i iaith gyffredin — hepgorir rhywfaint o'r manwl gywirdeb.",
        [P2.lossRegisterKid]: 'Symleiddiwyd ar gyfer darllenwyr iau — hepgorir llawer o fanylion.',
        [P2.lossProfileDetail]: 'Crynhoir y proffil llawn Gwarant / Empirig / Moddol yma.',
        [P2.lossConflictDetail]: "Mae manylion y gwrthdaro wedi'u byrhau yn y golwg hwn.",
        [P2.laneVerified]: "Wedi'i wirio",
        [P2.laneUnverified]: 'Heb ei wirio',
        [P2.laneConflict]: "Gwrthdaro wedi'i brofi",
        [P2.constraintLogic]: 'Rhesymeg',
        [P2.constraintLightBends]: "Mae golau'n plygu mewn maes disgyrchiant",
        [P2.constraintParallelPostulate]: 'Y postwlad paralel',
        [P2.claimScalarGravity]: 'Damcaniaeth ddisgyrchiant sgalar',
        [P2.claimNonEuclidean]: 'Geometreg anewclidaidd',
        [P2.claimSubstrateSr]: 'Dehongliad is-haen o berthnasedd arbennig',
        [P2.edgeExtObservation]: 'Arsylwad allanol',
        [P2.edgeResourceSample]: 'Adnodd cyfrifiadurol a rennir',
        [P2.edgeHeuristicSample]: 'Arweiniad heuristig',
        [P2.edgeSharedSample]: 'Sylfaen a rennir',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Cyfyngiadau',
        [P2.sectionEdges]: 'Ymylon',
        [P2.sectionFrame]: 'Ffrâm',
        [P2.sectionExposure]: 'Amlygiad',
        [P2.statusAdoptedFormal]: 'Mabwysiadwyd (ffurfiol)',
        [P2.statusAdoptedInterpretive]: 'Mabwysiadwyd (dehongliadol)',
        [P2.statusSuspended]: 'Ataliwyd',
        [P2.edgeEffectConstraint]: 'Yn ffurfio cyfyngiad (gall wrthbrofi)',
        [P2.edgeEffectResource]: 'Adnodd — dim effaith ar ddyfarniadau',
        [P2.edgeEffectHeuristic]: 'Heuristig — dim effaith ar ddyfarniadau',
        [P2.edgeEffectShared]: 'Sylfaen a rennir — dim effaith ar ddyfarniadau',
        [P2.frameActive]: '{count, plural, =0 {Dim cyfyngiadau gweithredol} zero {Dim cyfyngiad gweithredol} one {# cyfyngiad gweithredol} two {# gyfyngiad gweithredol} few {# chyfyngiad gweithredol} many {# chyfyngiad gweithredol} other {# cyfyngiad gweithredol}}',
        [P2.exposureNone]: 'Dim dibyniaethau dehongliadol',
        [P2.exposureDep]: 'Yn dibynnu ar {id}',
        [P2.laneTrackMachine]: 'Peiriant',
        [P2.laneTrackExpert]: 'Arbenigwr',
        [P2.laneTrackCommunity]: 'Cymuned',
        [P2.expertAudited]: 'Archwiliwyd',
        [P2.expertInProgress]: 'Ar y gweill',
        [P2.expertNotReviewed]: 'Heb ei adolygu',
        [P2.communityFlags]: "Mae baneri'n bodoli",
        [P2.communityNone]: 'Dim baneri',
        [P2.controlChallenge]: 'Herio',
        [P2.controlActivate]: 'Gweithredu',
        [P2.controlMakeInterpretive]: 'Gwneud yn ddehongliadol',
        [P2.controlImportEdge]: 'Mewnforio ymyl',
        [P2.controlPromote]: 'Dyrchafu',
        [P2.controlUndo]: 'Dadwneud',
        [P2.controlRedo]: 'Ailwneud',
        [P2.controlRegister]: 'Cofrestru',
        [P2.promoteRejected]: 'Gwrthodwyd y dyrchafiad',
        [P2.promoteAccepted]: 'Derbyniwyd y dyrchafiad',
        [P2.promoteReasonRefutationTerminal]: 'Ni all prawf wella gwrthbrofiad.',
        [P2.promoteReasonKeystone]: 'Mae dyrchafiad allweddol angen cefnogaeth empirig.',
        [P2.promoteReasonEmpiricalNotFixed]: "Nid yw'r math empirig wedi'i sefydlu.",
        [P2.promoteReasonModalNotFixed]: "Nid yw'r radd foddol wedi'i sefydlu.",
        [P2.promoteReasonWarrantNotImproved]: "Nid yw'r radd warant wedi gwella.",
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=cy.js.map